import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type GeneratedSuggestion,
  useGenerateSuggestions,
} from "@/hooks/use-recommendation-generation";
import type { ActivityCategory, RecommendationSource } from "@/lib/enums";
import { cn } from "@/lib/utils";

export interface PickedSuggestion {
  title: string;
  note: string | null;
  category: string;
  source: RecommendationSource;
}

interface GeneratedSuggestionOptionsProps {
  entryId: string;
  category: ActivityCategory;
  onPick: (picked: PickedSuggestion) => void;
}

/**
 * EPIC-009 (WI-025): shows the 3 suggestions generated for the given entry +
 * category as pickable options. Owns its own fetch (mirrors WidgetSelect /
 * AsyncCombobox wrappers — see .claude/rules/frontend.md). Mounted keyed by
 * `${entryId}-${category}` in ActivitySuggestionForm, so an entry/category
 * change remounts this fresh (new fetch, cleared selection) with no
 * useEffect needed.
 */
export function GeneratedSuggestionOptions({
  entryId,
  category,
  onPick,
}: GeneratedSuggestionOptionsProps) {
  const { data, isLoading, isError, refetch } = useGenerateSuggestions(entryId, category);
  const [pickedIndex, setPickedIndex] = useState<number | null>(null);

  function handlePick(suggestion: GeneratedSuggestion, index: number) {
    if (!data) return;
    setPickedIndex(index);
    onPick({
      title: suggestion.title,
      note: suggestion.note,
      category: suggestion.category ?? category,
      source: data.source,
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-dashed border-border p-3 text-sm text-destructive">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span className="flex-1">Couldn't load suggestions.</span>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  const suggestions = data?.suggestions ?? [];

  // WI-027: generation succeeded but returned nothing to pick from.
  if (suggestions.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
        No suggestions available for this category yet. Try a different category, or write your own
        idea below.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Pick a suggestion to fill in the fields below, or write your own.
      </p>
      <div className="grid gap-2">
        {suggestions.map((suggestion, index) => {
          const selected = pickedIndex === index;
          return (
            <Card
              key={suggestion.id ?? suggestion.title}
              role="button"
              tabIndex={0}
              onClick={() => handlePick(suggestion, index)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handlePick(suggestion, index);
                }
              }}
              className={cn(
                "cursor-pointer py-3 transition-colors",
                selected ? "border-primary bg-primary/5" : "hover:border-primary/50",
              )}
            >
              <CardContent className="px-3">
                <p className="text-sm font-medium">{suggestion.title}</p>
                {suggestion.note && (
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                    {suggestion.note}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
